import { useCallback, useContext } from "react";
import createPersistedState from "./createPersistedState";
import { StoreContext } from "./store";
import { Tenant } from "./types";
import useData from "./useData";
const useTenantIdState = createPersistedState<string>("tenant_id");

export interface TenantsResponse {
  tenants: Tenant[];
}

export const shortTenantDisplayName = (tenant: Tenant): string => {
  return tenantDisplayName(tenant, true);
};

export const tenantDisplayName = (tenant: Tenant, short = false): string => {
  if (tenant.kind == "sandbox") {
    if (tenant.sandbox_owned_by_current_user) {
      return short ? "Personal" : "Your Personal Organization";
    } else {
      const owner = tenant.sandbox_owner_name || "Someone else";
      return short ? owner : `${owner}'s Personal Organization`;
    }
  } else {
    return tenant.name || tenant.id;
  }
};

export const longTenantDisplayName = (tenant: Tenant): string => {
  if (tenant.kind == "sandbox") {
    const whose = tenant.sandbox_owned_by_current_user
      ? "Your"
      : `${tenant.sandbox_owner_name || "Someone else"}'s`;
    return `${whose} Personal Organization`;
  } else {
    const tenant_name = tenant.name || tenant.id;
    return `${tenant_name} Organization`;
  }
};

const useTenant = () => {
  const { forceTenantSwitcher, setForceTenantSwitcher } =
    useContext(StoreContext);
  const [storedTenantId, setStoredTenantId] = useTenantIdState("");
  const { data, refresh } = useData<TenantsResponse>("/web/tenants");
  const tenants = data?.tenants || [];

  const setTenant = useCallback(
    (tenant) => {
      setStoredTenantId(tenant.id);
      setForceTenantSwitcher(false);
    },
    [setStoredTenantId, setForceTenantSwitcher]
  );

  const switchTenant = useCallback(() => {
    setForceTenantSwitcher(true);
  }, [setForceTenantSwitcher]);

  const selectedTenantIndex = tenants.findIndex(
    (tenant) => tenant.id === storedTenantId
  );
  const tenant = tenants[selectedTenantIndex] || tenants[0];

  // Show the switcher if there is not a saved tenant ID in local storage AND
  // there are more than 1 tenants
  const shouldShowSwitcher =
    forceTenantSwitcher || (tenants.length > 1 && selectedTenantIndex < 0);

  return {
    shouldShowSwitcher,
    tenant: tenant ? tenant : null,
    tenants,
    setTenant,
    switchTenant,
    refresh,
  };
};

export default useTenant;
